import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
    fetchDicomCancelRetrievals,
    fetchDicomRetryRetrieval,
    usePatientsGetRetrievals,
} from '~/backend/connector-next-api/connector-next-components';
import {
    IMPORT_PROGRESS_POLL_MS,
    seriesRetrievals,
    summariseRetrievals,
    type RetrievalSummary,
    type SeriesRetrieval,
} from '../model';

export type PatientRetrievals = {
    retrievals: readonly SeriesRetrieval[];
    summary: RetrievalSummary;
    isLoading: boolean;
    isError: boolean;
    /** Cancels the named retrievals; the connector cancels only those still queued. */
    cancel: (retrievalIds: readonly string[]) => void;
    cancelling: boolean;
    retry: (retrievalId: string) => void;
    /** The retrieval a retry is being requested for, while it is. */
    retryingId?: string;
    /** The last cancel or retry request failed to reach the connector or was refused by it. */
    actionFailed: boolean;
};

/**
 * One patient's series retrievals as the connector records them, polled while any is still queued
 * or downloading, or while they cannot be read, and left alone once all have settled. A failed read
 * says nothing about whether anything is still moving, so it is asked again rather than taken as
 * settled.
 *
 * A cancel or retry refreshes everything under the patients key — the list row's counts, the
 * patient's datasets and these records — because each can change all three.
 */
export function usePatientRetrievals(patientId: string): PatientRetrievals {
    const queryClient = useQueryClient();
    const query = usePatientsGetRetrievals(
        { pathParams: { id: patientId } },
        {
            refetchInterval: (current) =>
                current.state.status === 'error' ||
                summariseRetrievals(seriesRetrievals(current.state.data)).active
                    ? IMPORT_PROGRESS_POLL_MS
                    : false,
        }
    );

    const retrievals = useMemo(
        () => seriesRetrievals(query.data),
        [query.data]
    );
    const summary = useMemo(
        () => summariseRetrievals(retrievals),
        [retrievals]
    );

    const refresh = useCallback(
        () => queryClient.invalidateQueries({ queryKey: ['api', 'patients'] }),
        [queryClient]
    );

    const cancelMutation = useMutation({
        mutationFn: (retrievalIds: readonly string[]) =>
            fetchDicomCancelRetrievals({
                body: { retrievalIds: [...retrievalIds] },
            }),
        onSettled: refresh,
    });

    const retryMutation = useMutation({
        mutationFn: (retrievalId: string) =>
            fetchDicomRetryRetrieval({ pathParams: { retrievalId } }),
        onSettled: refresh,
    });

    const { mutate: cancelMutate } = cancelMutation;
    const { mutate: retryMutate } = retryMutation;
    const cancel = useCallback(
        (retrievalIds: readonly string[]) => {
            if (retrievalIds.length > 0) {
                cancelMutate(retrievalIds);
            }
        },
        [cancelMutate]
    );
    const retry = useCallback(
        (retrievalId: string) => retryMutate(retrievalId),
        [retryMutate]
    );

    return {
        retrievals,
        summary,
        isLoading: query.isLoading,
        isError: query.isError,
        cancel,
        cancelling: cancelMutation.isPending,
        retry,
        retryingId: retryMutation.isPending
            ? retryMutation.variables
            : undefined,
        actionFailed: cancelMutation.isError || retryMutation.isError,
    };
}
